/**
 * Sticky A/B variant, resolved on the client (see abClientVariant.ts for why the
 * client and not the server behind CloudFront).
 *
 * Two coordinated pieces keep the swap flash-free:
 *
 * 1. <AbAntiFlickerGate> renders a tiny BLOCKING inline script + style into the
 *    SSR HTML. On a hard page load the browser runs it during parse, BEFORE the
 *    content region below it paints. It creates/reads the `ab_visitor` cookie,
 *    picks the visitor's variant with the same weighted-pick math the hook uses,
 *    and if that variant is NOT the parent it hides the `.ab-gate` content region
 *    (visibility:hidden, so no CLS) until React reveals it. Visitors bucketed to
 *    the parent are never hidden, so they take no LCP hit. A 2.5s timeout reveals
 *    regardless, so a broken/slow bundle degrades to the parent rather than a
 *    blank page.
 *
 * 2. useStickyAbVariant() renders the parent on the first pass (matching SSR, so
 *    hydration does not mismatch) and swaps to the chosen variant inside a
 *    useLayoutEffect, which runs before the browser paints. On soft (SPA)
 *    navigations there is no server-HTML pre-paint, so this pre-paint swap alone
 *    prevents the flash; the blocking script only matters on hard loads. Because
 *    the script sets the cookie first, the hook reads the same id and picks the
 *    same variant, so the class the script added is always the one React clears.
 */

import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
  type BuilderVariationMap,
  getOrCreateVisitorId,
  pickVariantId,
  pinBuilderTestCookie,
} from 'utils/builder/abClientVariant';
import useBuilderVariantStore from 'utils/builder/useBuilderVariantStore';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BuilderContent = Record<string, any> & { id?: string; data?: any; variations?: BuilderVariationMap };

const HOLD_CLASS = 'ab-hold';
const GATE_CLASS = 'ab-gate';
// visibility (not display) keeps the region's box so the reveal causes no layout shift.
const GATE_STYLE = `html.${HOLD_CLASS} .${GATE_CLASS}{visibility:hidden}`;

const getVariations = (content: BuilderContent | null | undefined): BuilderVariationMap | undefined => {
  const variations = content?.variations;

  return variations && Object.keys(variations).length > 0 ? variations : undefined;
};

/**
 * Returns the content the page should actually render for this visitor. On the
 * server and the first client render it is the parent; a useLayoutEffect then
 * swaps in the visitor's sticky variant before paint. Also publishes the resolved
 * variant id + phase to the store for Marketo attribution and form gating.
 */
export const useStickyAbVariant = (pageData: BuilderContent | null | undefined) => {
  const setVariantId = useBuilderVariantStore(s => s.setVariantId);
  const setAbPhase = useBuilderVariantStore(s => s.setAbPhase);

  const parentId = pageData?.id;
  const variations = getVariations(pageData);
  const isAbActive = !!parentId && !!variations;

  // Start on the parent so SSR and the first hydration render agree.
  const [activeId, setActiveId] = useState<string | undefined>(parentId);

  // Resolve the variant DURING RENDER on the client (once), before the child
  // BuilderComponent mounts. Builder fires its Insights impression from that
  // child's componentDidMount, which runs BEFORE this component's useLayoutEffect,
  // and reads the `builder.tests.<contentId>` cookie to attribute it. Setting the
  // cookie here (not just in the effect) means the impression lands on the chosen
  // variant on soft navigations too, not Default. On a hard load the blocking
  // snippet has already set it. No-op on the server (no document).
  const resolvedRef = useRef<string | undefined>(undefined);
  if (typeof document !== 'undefined' && isAbActive && parentId && variations && resolvedRef.current === undefined) {
    const visitorId = getOrCreateVisitorId();
    resolvedRef.current = pickVariantId(visitorId, parentId, variations);
    pinBuilderTestCookie(parentId, resolvedRef.current);
  }

  useLayoutEffect(() => {
    if (!isAbActive || !parentId || !variations) {
      // Non-A/B page: nothing to resolve, let forms load immediately.
      setVariantId('');
      setAbPhase('resolved');

      return;
    }

    setAbPhase('pending');
    const chosen = resolvedRef.current ?? pickVariantId(getOrCreateVisitorId(), parentId, variations);
    setActiveId(chosen);
    // Attribution: stamp the variation id, or the parent id for the parent share.
    setVariantId(chosen);
    setAbPhase('resolved');
    // Reveal the content the blocking script may have hidden for a variation.
    if (typeof document !== 'undefined') {
      document.documentElement.classList.remove(HOLD_CLASS);
    }
    // Resolve once on mount; the visitor id is stable so re-running cannot change it.
  }, []);

  const activeContent = useMemo<BuilderContent | undefined>(() => {
    if (!isAbActive || !parentId || !variations || !activeId || activeId === parentId) {
      return pageData ?? undefined;
    }
    const variationData = variations[activeId]?.data;
    if (!variationData) {
      return pageData ?? undefined;
    }

    // Keep content.id = parentId (Builder tracking keys on it); stamp the variation
    // ids so downstream impression/attribution see the rendered variant.
    return { ...pageData, data: variationData, variationId: activeId, testVariationId: activeId };
  }, [isAbActive, parentId, variations, activeId, pageData]);

  return { activeContent, activeData: activeContent?.data, isAbActive };
};

/**
 * Blocking anti-flicker snippet + style. Renders nothing for non-A/B pages. Must
 * appear in the tree BEFORE the `.ab-gate` content wrapper so it runs (and the
 * style exists) before that region is parsed/painted on a hard load.
 */
export const AbAntiFlickerGate = ({ pageData }: { pageData: BuilderContent | null | undefined }) => {
  const parentId = pageData?.id;
  const variations = getVariations(pageData);
  if (!parentId || !variations) {
    return null;
  }

  // [variationId, testRatio][] for the vanilla-JS weighted pick.
  const variationTuples = Object.keys(variations).map(key => [
    variations[key].id || key,
    variations[key].testRatio || 0,
  ]);

  const snippet = `(function(){try{
var P=${JSON.stringify(parentId)},V=${JSON.stringify(variationTuples)};
var m=document.cookie.match(/(?:^|; )ab_visitor=([^;]+)/);var id=m?decodeURIComponent(m[1]):'';
if(!id){id=(window.crypto&&crypto.randomUUID)?crypto.randomUUID():(Date.now().toString(36)+Math.random().toString(36).slice(2));
document.cookie='ab_visitor='+encodeURIComponent(id)+'; Path=/; Max-Age=31536000; SameSite=Lax';}
var s=id+'|'+P,h=2166136261;for(var i=0;i<s.length;i++){h^=s.charCodeAt(i);h=Math.imul(h,16777619);}
var b=(h>>>0)/4294967296,c=0,ch=P;for(var j=0;j<V.length;j++){c+=V[j][1]||0;if(b<c){ch=V[j][0];break;}}
document.cookie='builder.tests.'+P+'='+ch+'; Path=/; Max-Age=31536000; SameSite=Lax';
if(ch!==P){document.documentElement.classList.add('${HOLD_CLASS}');}
setTimeout(function(){document.documentElement.classList.remove('${HOLD_CLASS}');},2500);
}catch(e){}})();`;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: GATE_STYLE }} />
      <script dangerouslySetInnerHTML={{ __html: snippet }} />
    </>
  );
};

export { GATE_CLASS };
